import React, { useState, useRef, useEffect } from "react";
import { Button, Input, Avatar, Tooltip } from "antd";
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  RobotOutlined,
  DeleteOutlined,
  ExpandAltOutlined,
  ShrinkOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { postData, fetchData } from "../../apiservice/apis";
import "./ChatAgent.css";

const GREETING = {
  role: "assistant",
  content:
    "Hi! I'm your ForthERP assistant. Ask me anything about your business data — latest invoices, top-selling products, stock levels, revenue trends — or how to use the system.",
};

const ChatAgent = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [usage, setUsage] = useState({ used: 0, limit: null, plan: null, upgradePlan: null });
  const [fabPos, setFabPos] = useState(null); // null = CSS default; { left, top } when dragged
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const dragState = useRef(null);

  useEffect(() => {
    if (open) {
      setHasNew(false);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        inputRef.current?.focus();
      }, 100);
      // Fetch current month's usage whenever the panel opens
      fetchData("/chat/usage")
        .then((res) => { if (res?.data) setUsage({ used: res.data.used, limit: res.data.limit, plan: res.data.plan, upgradePlan: res.data.upgradePlan }); })
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (messages.length > 1) {
      setHasNew(true);
    }
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: "user", content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput("");
    setLoading(true);

    try {
      const history = updated.slice(1, -1);
      const res = await postData("/chat/data", { message: text, history }, { timeout: 60000 });
      if (res.data?.usage) setUsage({ used: res.data.usage.used, limit: res.data.usage.limit });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data?.reply || "Sorry, I couldn't get a response." },
      ]);
    } catch (err) {
      const msg = err?.message || err?.response?.message || "Sorry, something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: msg },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([GREETING]);
    setInput("");
  };

  const onFabMouseDown = (e) => {
    if (e.button !== 0) return;
    const rect = fabRef.current.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      moved: false,
    };

    const onMouseMove = (ev) => {
      const dx = ev.clientX - dragState.current.startX;
      const dy = ev.clientY - dragState.current.startY;
      if (!dragState.current.moved && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        dragState.current.moved = true;
      }
      if (dragState.current.moved) {
        const FAB = 52;
        const newLeft = Math.max(8, Math.min(window.innerWidth - FAB - 8, dragState.current.startLeft + dx));
        const newTop = Math.max(8, Math.min(window.innerHeight - FAB - 8, dragState.current.startTop + dy));
        setFabPos({ left: newLeft, top: newTop });
      }
    };

    const onMouseUp = () => {
      if (!dragState.current?.moved) setOpen((o) => !o);
      dragState.current = null;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const getPanelStyle = () => {
    if (!fabPos) return {};
    const FAB = 52;
    const panelW = expanded ? 720 : 420;
    const panelH = expanded ? window.innerHeight - 108 : 560;
    let left = Math.max(8, Math.min(window.innerWidth - panelW - 8, fabPos.left + FAB - panelW));
    let top = fabPos.top - panelH - 12;
    if (top < 8) top = fabPos.top + FAB + 12;
    return { left, top, right: "auto", bottom: "auto" };
  };

  return (
    <>
      <Tooltip title={open ? "" : "Ask ForthERP Assistant"} placement="left">
        <button
          ref={fabRef}
          className={`chat-fab ${hasNew && !open ? "chat-fab--badge" : ""} ${open ? "chat-fab--open" : ""}`}
          onMouseDown={onFabMouseDown}
          aria-label="Open chat assistant"
          style={fabPos ? { left: fabPos.left, top: fabPos.top, right: "auto", bottom: "auto", cursor: "grab" } : {}}
        >
          {open ? <CloseOutlined /> : <MessageOutlined />}
        </button>
      </Tooltip>

      {open && (
        <div className={`chat-panel${expanded ? " chat-panel--expanded" : ""}`} style={getPanelStyle()}>
          <div className="chat-panel__header">
            <div className="chat-panel__header-left">
              <Avatar icon={<RobotOutlined />} size={36} className="chat-panel__avatar" />
              <div>
                <div className="chat-panel__title">ForthERP Assistant</div>
                <div className="chat-panel__subtitle">Powered by AI</div>
                {usage.limit !== null && (
                  <div className={`chat-panel__usage${usage.limit > 0 && usage.used >= usage.limit ? " chat-panel__usage--exhausted" : ""}`}>
                    {`${Math.max(0, usage.limit - usage.used)} / ${usage.limit} queries remaining`}
                  </div>
                )}
              </div>
            </div>
            <div className="chat-panel__header-actions">
              <Tooltip title="Clear chat">
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={clearChat}
                  className="chat-panel__icon-btn"
                />
              </Tooltip>
              <Tooltip title={expanded ? "Shrink" : "Expand"}>
                <Button
                  type="text"
                  size="small"
                  icon={expanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
                  onClick={() => setExpanded((e) => !e)}
                  className="chat-panel__icon-btn"
                />
              </Tooltip>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => setOpen(false)}
                className="chat-panel__icon-btn"
              />
            </div>
          </div>

          <div className="chat-panel__messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-msg chat-msg--${msg.role}`}>
                {msg.role === "assistant" && (
                  <Avatar
                    size={28}
                    icon={<RobotOutlined />}
                    className="chat-panel__avatar chat-msg__avatar"
                  />
                )}
                <div className="chat-msg__bubble">
                  {msg.role === "assistant" ? (
                    <div className="chat-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown></div>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="chat-msg chat-msg--assistant">
                <Avatar
                  size={28}
                  icon={<RobotOutlined />}
                  className="chat-panel__avatar chat-msg__avatar"
                />
                <div className="chat-msg__bubble chat-msg__bubble--typing">
                  <span className="chat-dot" />
                  <span className="chat-dot" />
                  <span className="chat-dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {usage.limit > 0 && usage.upgradePlan && usage.used >= usage.limit && (
            <div className="chat-panel__limit-banner chat-panel__limit-banner--hard">
              Monthly limit of {usage.limit} queries reached.{" "}
              Upgrade to {usage.upgradePlan} for more queries.
            </div>
          )}
          {usage.limit > 0 && usage.upgradePlan && usage.used < usage.limit && usage.used / usage.limit >= 0.8 && (
            <div className="chat-panel__limit-banner chat-panel__limit-banner--warn">
              Running low — {Math.max(0, usage.limit - usage.used)}/{usage.limit} queries remaining.{" "}
              Upgrade to {usage.upgradePlan} for more.
            </div>
          )}

          <div className="chat-panel__input-area">
            <Input.TextArea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question… (Enter to send)"
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={loading}
              className="chat-panel__textarea"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={sendMessage}
              disabled={!input.trim() || loading || (usage.limit > 0 && usage.used >= usage.limit)}
              className="chat-panel__send-btn"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ChatAgent;
